/**
 * ============================================================================
 * MIGRATED FILE — DO NOT EDIT
 * ============================================================================
 *
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/playlistContent/components/PlaylistNavigationDropdown.tsx
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 *
 * @migrated-to academy-content-ui
 * ============================================================================
 */

import { useCallback } from 'react';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { BlankIcon } from 'foundations-assets/icons/components/BlankIcon';
import { SuccessIcon } from 'foundations-assets/icons/components/SuccessIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { H7 } from 'foundations-components/transitional/components/H7';
import { HR } from 'foundations-components/transitional/components/HR';
import { IconCircle } from 'foundations-components/transitional/components/IconCircle';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { StepIndicator } from 'foundations-components/transitional/components/StepIndicator';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { View } from 'foundations-components/transitional/components/View';
import { useTheme } from 'foundations-theming/utils';
import { ACRouterLink } from 'academy-common-lib/components/ACRouterLink';
import { getListingUrl } from 'academy-common-lib/features/learningContentCard/routing/RoutingUtils';
import UsageTracker from '../../../utils/UsageTracker';
import { useIncorrectPlaylistRedirect } from '../hooks.migrated';
import { useFilteredRestrictedPlaylistItems } from '../../playlistItems';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PlaylistNavigationDropdown = ({
  item: {
    id,
    type,
    playlistId
  },
  playlist,
  playlistProgress,
  buttonTitle
}) => {
  var _displayedPlaylistIte;
  useIncorrectPlaylistRedirect({
    id,
    type: type,
    playlistId
  });
  const getStepUrl = useCallback((itemId, itemType, language) => getListingUrl({
    id: itemId,
    type: itemType,
    language,
    appearsIn: 'playlist-dropdown',
    isStarted: true,
    playlistId
  }), [playlistId]);
  const displayedPlaylistItems = useFilteredRestrictedPlaylistItems((playlist === null || playlist === void 0 ? void 0 : playlist.items) || []);
  const StepIcon = useCallback(({
    circleSize,
    index,
    stepIndex,
    defaultIcon
  }) => {
    if (!playlistProgress || !displayedPlaylistItems) return defaultIcon;
    const {
      itemId,
      type: itemType
    } = displayedPlaylistItems[index];
    let status = 'NOT_STARTED';
    const trackProgress = playlistProgress.itemsProgress.find(asset => asset.itemType === itemType && asset.itemId === itemId);
    if (trackProgress) {
      status = readUnhandledEnumCase(trackProgress.status);
    }
    const isActive = index === stepIndex;
    const isCompleted = status === 'COMPLETED';
    let backgroundColor = 'fill-field-default-alt';
    let borderColor = 'border-core-default';
    let color = 'icon-secondary-default';
    if (isActive) {
      borderColor = 'border-brand-default';
    } else if (isCompleted) {
      backgroundColor = 'fill-brand-default';
      color = 'icon-core-on-fill-default';
      borderColor = 'border-brand-default';
    }
    return /*#__PURE__*/_jsx(IconCircle, {
      backgroundColor: backgroundColor,
      borderColor: borderColor,
      borderWidth: "200",
      color: color,
      icon: isCompleted ? SuccessIcon : BlankIcon,
      legacy: true,
      size: circleSize
    });
  }, [displayedPlaylistItems, playlistProgress]);
  const handleOnStepClick = index => {
    if (!displayedPlaylistItems) return;
    const {
      itemId
    } = displayedPlaylistItems[index];
    UsageTracker.trackStandalone('Playlist Interaction', {
      action: 'navigator-dropdown-item-clicked',
      playlistId,
      id,
      type: type,
      to: itemId
    });
  };
  const theme = useTheme();
  const stepIndex = displayedPlaylistItems === null || displayedPlaylistItems === void 0 ? void 0 : displayedPlaylistItems.findIndex(({
    itemId
  }) => itemId === id);
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx("div", {
      style: {
        marginBlockEnd: `-${theme.space['100']}`,
        visibility: !playlist ? 'hidden' : 'visible'
      },
      children: /*#__PURE__*/_jsx(Dropdown, {
        buttonText: /*#__PURE__*/_jsx(TruncateString, {
          initialWidth: "fit-content",
          matchContentWidth: true,
          children: buttonTitle || (playlist === null || playlist === void 0 ? void 0 : playlist.name)
        }),
        buttonUse: "link",
        "data-test-id": "playlist-navigator",
        onOpenComplete: () => {
          UsageTracker.track('playlistInteraction', {
            action: 'navigator-dropdown-open',
            playlistId,
            id,
            type: type
          });
        },
        placement: "top right",
        use: "unstyled",
        children: /*#__PURE__*/_jsxs(Flex, {
          "data-test-id": "playlist-navigator-dropdown-popover",
          flexDirection: "column",
          children: [/*#__PURE__*/_jsx(H7, {
            marginBlockEnd: "400",
            marginBlockStart: "200",
            marginInline: "400",
            children: playlist === null || playlist === void 0 ? void 0 : playlist.name
          }), /*#__PURE__*/_jsx(HR, {
            distance: "flush"
          }), /*#__PURE__*/_jsx(ScrollContainer, {
            maxHeight: "50vh",
            children: /*#__PURE__*/_jsx(StepIndicator, {
              StepIconComponent: StepIcon,
              direction: "vertical",
              margin: "0",
              onStepClick: handleOnStepClick,
              paddingBlockStart: "300",
              paddingInline: "400",
              stepIndex: stepIndex,
              stepNames: (_displayedPlaylistIte = displayedPlaylistItems === null || displayedPlaylistItems === void 0 ? void 0 : displayedPlaylistItems.map(({
                itemId,
                type: itemType,
                name: itemName,
                language
              }, index) => {
                const isCurrentStep = stepIndex === index;
                return /*#__PURE__*/_jsx(View, {
                  marginBlockStart: isCurrentStep ? '-25' : '-100',
                  children: /*#__PURE__*/_jsx(ACRouterLink, {
                    "data-test-id": "playlist-navigator-item",
                    "data-test-item-id": itemId,
                    "data-test-item-type": itemType,
                    to: getStepUrl(itemId, itemType, readUnhandledEnumCase(language).toLowerCase()),
                    use: "unstyled",
                    children: /*#__PURE__*/_jsx(View, {
                      typography: isCurrentStep ? 'heading-50' : 'body-100',
                      children: itemName
                    })
                  })
                }, itemName);
              })) !== null && _displayedPlaylistIte !== void 0 ? _displayedPlaylistIte : []
            })
          }), /*#__PURE__*/_jsx(HR, {
            distance: "flush"
          }), /*#__PURE__*/_jsx(View, {
            marginBlockStart: "200",
            marginInline: "400",
            children: /*#__PURE__*/_jsx(ACRouterLink, {
              onClick: () => {
                UsageTracker.trackStandalone('Playlist Interaction', {
                  action: 'navigator-dropdown-view-playlist-clicked',
                  playlistId,
                  id,
                  type: type
                });
              },
              to: `/saved-items/playlists/${playlistId}`,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.playlists.viewPlaylist"
              })
            })
          })]
        })
      })
    })
  );
};

/**
 * @migrated-to academy-content-ui
 * @deprecated
 * This file has been migrated to academy-content-ui.
 * Canonical source: academy-content-ui/static/js/features/playlistContent/components/PlaylistNavigationDropdown.tsx
 *
 * This .migrated copy is retained only for LearningCenterUI consumers that
 * have not yet moved to academy-content-ui. All changes should be made in
 * academy-content-ui first, then synced here if still needed.
 *
 * This copy will be deleted when LearningCenterUI is shut down.
 */
export default PlaylistNavigationDropdown;